/**
 * The one entity and state normaliser for state-tax code.
 *
 * It replaces the copies that used to live in `apps/api/src/state-tax/customer.ts`
 * (two), `apps/api/src/state-tax/checkout-evidence.ts`,
 * `apps/app/src/features/state-tax/lib/state-tax-pricing.ts` and
 * `apps/app/src/features/state-tax/domain/rules/eligibility.rules.ts`. They
 * disagreed: `c corp` counted as a corporation in three of them, `corporation`
 * in three others (not the same three), and `c_corp` only in the Stripe
 * webhook copy. Every spelling any of them accepted is accepted here.
 *
 * State tax cares about two entity kinds only. A multi-member LLC is an LLC
 * here; the single- vs multi-member split matters to federal tax, not to a
 * state annual report.
 */

export type StateCode = "WY" | "DE";
export type EntityKind = "llc" | "corporation";

/** Lower-case and drop spaces, dots, dashes and underscores: "C-Corp." -> "ccorp". */
function squash(raw: string): string {
  return raw.toLowerCase().replace(/[\s._-]+/g, "");
}

/**
 * `corporation` for any C-corp or S-corp spelling, `llc` for everything else
 * (including unknown, empty and non-string values, matching the old default).
 */
export function normalizeEntity(raw: unknown): EntityKind {
  if (typeof raw !== "string") return "llc";
  const key = squash(raw);
  if (key === "inc" || key === "incorporated") return "corporation";
  if (key.includes("corp")) return "corporation";
  return "llc";
}

/** `WY` or `DE` from a code or a state name in any case; anything else is null. */
export function normalizeState(raw: unknown): StateCode | null {
  if (typeof raw !== "string") return null;
  const key = raw.trim().toLowerCase();
  if (key === "wy" || key === "wyoming") return "WY";
  if (key === "de" || key === "delaware") return "DE";
  return null;
}
